import { create } from 'zustand';
import axios from 'axios';

export interface ReferenceOption {
  key: string;
  label: string;
}

export interface ReferenceStore {
  options: Record<string, ReferenceOption[]>;
  isLoading: boolean;
  fetchConfig: () => Promise<void>;
  addOption: (category: string, label: string) => Promise<void>;
  renameOption: (category: string, key: string, label: string) => Promise<void>;
  deleteOption: (category: string, key: string) => Promise<void>;
}

export const useReferenceStore = create<ReferenceStore>((set) => ({
  options: {},
  isLoading: false,

  fetchConfig: async () => {
    set({ isLoading: true });
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reference/config`);
      set({ options: res.data.config, isLoading: false });
    } catch (err) {
      console.error("Failed to fetch global config", err);
      set({ isLoading: false });
    }
  }, // <--- Check if this comma was missing!

  addOption: async (category: string, label: string) => {
    // reference_options.key is unique GLOBALLY, not per category, and the
    // backend returns the existing row on a key hit whatever category it sits
    // in. Without this prefix, adding "MBA" to management_qualification would
    // hand back the nurse list's "MBA" and the option would disappear from this
    // category on the next reload. Prefixing keeps same-named entries in
    // different lists genuinely independent.
    //
    // Only affects newly added options — existing rows keep their keys, and
    // ReferenceDropdown already blocks adding a label that's in this category,
    // so this can't produce duplicates within one list.
    const slug = label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '_');
    const key = `${category}_${slug}`.substring(0, 200);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reference/add`, {
        category,
        key,
        label
      });
      set((state: ReferenceStore) => ({
        options: {
          ...state.options,
          [category]: [...(state.options[category] || []), res.data]
        }
      }));
    } catch (err) {
      throw err;
    }
  },

  renameOption: async (category: string, key: string, label: string) => {
    // Key stays as it is — see the backend note. Only the displayed text moves,
    // so fields that store the key (the multi-selects) follow the rename, while
    // fields that stored the old text keep showing it until they're re-picked.
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/api/reference/${encodeURIComponent(key)}`,
      { label }
    );
    set((state: ReferenceStore) => ({
      options: {
        ...state.options,
        [category]: (state.options[category] || []).map((o) =>
          o.key === key ? { ...o, label: res.data.label } : o
        )
      }
    }));
  },

  deleteOption: async (category: string, key: string) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/reference/${encodeURIComponent(key)}`);
    set((state: ReferenceStore) => ({
      options: {
        ...state.options,
        [category]: (state.options[category] || []).filter((o) => o.key !== key)
      }
    }));
  }
}));